import { TBooking, TServiceTicket } from "~/types";

export type ClientBookingRelationship = 'main-client' | 'service-client' | 'none';

export interface FilteredBookingData {
  booking: TBooking;
  relationship: ClientBookingRelationship;
  visibleServiceTickets: TServiceTicket[];
  canViewFullBooking: boolean;
}

/**
 * Determines the client's relationship to a booking and what information they can see
 */
export function getClientBookingRelationship(
  booking: TBooking,
  currentClientId: string
): FilteredBookingData {
  // Check if client is the main booking client
  const isMainClient = booking.clientId === currentClientId;

  if (isMainClient) {
    return {
      booking,
      relationship: 'main-client',
      visibleServiceTickets: booking.serviceTicket || [],
      canViewFullBooking: true,
    };
  }

  // Check if logged-in user is the parent of the booking's main client (subclient booking)
  // In this case, show ALL service tickets for the booking
  const isParentOfBookingClient = booking.client?.parentClientId === currentClientId;
  if (isParentOfBookingClient) {
    return {
      booking,
      relationship: 'main-client',
      visibleServiceTickets: booking.serviceTicket || [],
      canViewFullBooking: true,
    };
  }

  // Check if client is a service client in any service ticket
  // OR if any service ticket belongs to a subclient of the current client
  const serviceTicketsForClient = (booking.serviceTicket || []).filter(
    ticket => {
      const isDirectServiceClient = ticket.clientId === currentClientId;
      const isSubclientService = ticket.client?.parentClientId === currentClientId;
      return isDirectServiceClient || isSubclientService;
    }
  );

  if (serviceTicketsForClient.length > 0) {
    // If any service ticket is for a subclient, treat as main-client relationship
    // (parent can see all services for their subclients)
    const hasSubclientServices = serviceTicketsForClient.some(
      ticket => ticket.client?.parentClientId === currentClientId
    );

    if (hasSubclientServices) {
      return {
        booking,
        relationship: 'main-client',
        visibleServiceTickets: serviceTicketsForClient,
        canViewFullBooking: true,
      };
    }

    return {
      booking,
      relationship: 'service-client',
      visibleServiceTickets: serviceTicketsForClient,
      canViewFullBooking: false,
    };
  }

  return {
    booking,
    relationship: 'none',
    visibleServiceTickets: [],
    canViewFullBooking: false,
  };
}

/**
 * Filters booking data for display based on client relationship
 */
export function getFilteredBookingForDisplay(
  booking: TBooking,
  currentClientId: string
): FilteredBookingData {
  const relationship = getClientBookingRelationship(booking, currentClientId);
  
  if (relationship.relationship === 'service-client') {
    // For service clients, create a filtered booking that only shows relevant information
    const filteredBooking: TBooking = {
      ...booking,
      // Only show the service tickets they are involved in
      serviceTicket: relationship.visibleServiceTickets,
      // Hide sensitive booking information
      payment: undefined,
      communications: [],
      notes: [],
      attachments: [],
    };
    
    return {
      ...relationship,
      booking: filteredBooking,
    };
  }
  
  return relationship;
}

/**
 * Gets display text for the booking based on client relationship
 */
export function getBookingDisplayInfo(filteredData: FilteredBookingData) {
  const { booking, relationship, visibleServiceTickets } = filteredData;
  
  if (relationship === 'service-client') {
    // For service clients, show their role in the service
    const serviceTypes = visibleServiceTickets.map(ticket => {
      const serviceType = ticket.serviceType.toLowerCase();
      if (serviceType.includes('collection')) {
        return 'Collection (Shipper)';
      } else if (serviceType.includes('delivery')) {
        return 'Delivery (Consignee)';
      } else {
        return `${serviceType} (Service Client)`;
      }
    });
    
    return {
      title: booking.referenceNumber,
      subtitle: `${serviceTypes.join(', ')} • ${booking.project?.name || 'No Project'}`,
      showLimitedBadge: true,
    };
  }
  
  // For main clients, show normal booking information
  return {
    title: booking.referenceNumber,
    subtitle: `${booking.project?.name || 'No Project'}`,
    showLimitedBadge: false,
  };
}
